/**
 * Click-to-define glossary. Any element with `.gloss[data-term]` becomes a
 * dotted-underline term that reveals a definition tooltip on click. Escape or an
 * outside click dismisses it.
 */

export const GLOSSARY: Record<string, { title: string; body: string }> = {
  pdfjs: {
    title: 'pdf.js',
    body: "Mozilla's PDF engine, running here in your browser to draw a picture of each page. It only reads your file locally — it never sends it anywhere.",
  },
  pdflib: {
    title: 'pdf-lib',
    body: 'A pure-JavaScript library that builds PDFs. Pagewell uses it to copy your chosen pages into a new file without re-encoding them.',
  },
  worker: {
    title: 'Web Worker',
    body: 'A background thread in your browser. Heavy work (rendering, assembling) runs there so the page stays smooth — and, being in your browser, still touches no server.',
  },
  lossless: {
    title: 'lossless',
    body: 'Pages are copied byte-for-byte from the original. Nothing is re-compressed, so text stays sharp and quality is never reduced.',
  },
  fsaccess: {
    title: 'File System Access API',
    body: 'A browser feature that lets a web page save a file straight to a folder you pick — no upload, no download quirks. Falls back to a normal download where unsupported.',
  },
  undo: {
    title: 'undo history',
    body: 'Every reorder, rotate and delete is remembered in this tab so you can step backwards with Cmd/Ctrl-Z and forwards with Shift-Cmd/Ctrl-Z.',
  },
};

let tip: HTMLElement | null = null;
let openFor: HTMLElement | null = null;

function ensureTip(): HTMLElement {
  if (!tip) {
    tip =
      document.getElementById('glossary-tip') ??
      (() => {
        const el = document.createElement('div');
        el.id = 'glossary-tip';
        el.className = 'glossary-tip';
        el.setAttribute('role', 'tooltip');
        el.hidden = true;
        document.body.appendChild(el);
        return el;
      })();
  }
  return tip;
}

function hide(): void {
  const t = ensureTip();
  t.hidden = true;
  openFor = null;
}

function showFor(anchor: HTMLElement): void {
  const term = anchor.dataset.term;
  if (!term) return;
  const def = GLOSSARY[term];
  if (!def) return;
  const t = ensureTip();
  t.innerHTML = '';
  const h = document.createElement('strong');
  h.textContent = def.title;
  const p = document.createElement('span');
  p.textContent = def.body;
  t.appendChild(h);
  t.appendChild(p);
  t.hidden = false;

  const r = anchor.getBoundingClientRect();
  const tipRect = t.getBoundingClientRect();
  let left = r.left + window.scrollX;
  let top = r.bottom + window.scrollY + 8;
  const maxLeft = window.scrollX + document.documentElement.clientWidth - tipRect.width - 12;
  if (left > maxLeft) left = Math.max(window.scrollX + 12, maxLeft);
  t.style.left = `${left}px`;
  t.style.top = `${top}px`;
  openFor = anchor;
}

/** Wire delegated click handling for all current and future `.gloss` terms. */
export function initGlossary(): void {
  ensureTip();

  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest<HTMLElement>('.gloss[data-term]');
    if (anchor) {
      e.preventDefault();
      if (openFor === anchor) {
        hide();
      } else {
        showFor(anchor);
      }
      return;
    }
    if (openFor && !target.closest('#glossary-tip')) hide();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openFor) hide();
  });

  window.addEventListener('resize', hide);
  window.addEventListener('scroll', hide, true);
}
